import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { ArrowLeft, Mail, AlertTriangle, Loader2 } from 'lucide-react';

/**
 * Verifizierungs-Code-Page (6-stellig). Wird angezeigt:
 *   - direkt nach Registrierung (purpose='register')
 *   - direkt nach Login bei aktiviertem 2FA (purpose='login')
 *
 * Props:
 *   - email: Empfänger-Adresse
 *   - purpose: 'register' | 'login'
 *   - onBack(): zurück zu Login/Register
 */
export default function VerifyCodePage({ email, purpose, onBack }) {
  const { verifyRegisterCode, verifyLoginCode, resendCode } = useAuth();
  const [digits, setDigits] = useState(['', '', '', '', '', '']);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendStatus, setResendStatus] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const refs = [useRef(), useRef(), useRef(), useRef(), useRef(), useRef()];

  useEffect(() => { refs[0].current?.focus(); }, []); // eslint-disable-line

  // Cooldown ticker for resend
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const submit = async (code) => {
    setError('');
    setLoading(true);
    const verifier = purpose === 'register' ? verifyRegisterCode : verifyLoginCode;
    const result = await verifier(email, code);
    setLoading(false);
    if (!result.success) {
      setError(result.error || 'Code ungültig.');
      // Felder leeren für neuen Versuch
      setDigits(['', '', '', '', '', '']);
      refs[0].current?.focus();
    }
    // success: AuthContext setzt user → Re-Render in AppShell führt zur App
  };

  const updateDigit = (i, val) => {
    const v = val.replace(/\D/g, '').slice(0, 1);
    const next = [...digits];
    next[i] = v;
    setDigits(next);
    if (v && i < 5) refs[i + 1].current?.focus();
    // Auto-submit, wenn alle 6 Stellen voll
    const code = next.join('');
    if (code.length === 6 && !loading) submit(code);
  };

  const onPaste = (e) => {
    e.preventDefault();
    const pasted = (e.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, 6);
    if (pasted.length === 0) return;
    const next = pasted.padEnd(6, '').split('').slice(0, 6);
    setDigits(next);
    if (pasted.length === 6) submit(pasted);
    else refs[Math.min(5, pasted.length)].current?.focus();
  };

  const onKeyDown = (i, e) => {
    if (e.key === 'Backspace' && !digits[i] && i > 0) refs[i - 1].current?.focus();
    if (e.key === 'ArrowLeft' && i > 0) refs[i - 1].current?.focus();
    if (e.key === 'ArrowRight' && i < 5) refs[i + 1].current?.focus();
  };

  const onResend = async () => {
    if (cooldown > 0 || loading) return;
    setResendStatus('');
    setError('');
    const result = await resendCode(email, purpose);
    if (result.success) {
      setResendStatus('Neuer Code wurde gesendet.');
      setCooldown(30);
    } else {
      setError(result.error || 'Code konnte nicht gesendet werden.');
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center px-4 relative">
      <button
        onClick={onBack}
        data-testid="verify-back-btn"
        className="absolute top-6 left-6 flex items-center gap-2 text-zinc-500 hover:text-zinc-200 text-sm font-mono transition-colors"
      >
        <ArrowLeft className="w-4 h-4" /> Zurück
      </button>

      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-blue-500/10 border border-blue-500/30 flex items-center justify-center">
            <Mail className="w-6 h-6 text-blue-400" />
          </div>
          <h1
            className="text-2xl sm:text-3xl font-bold mb-3 leading-tight"
            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
            data-testid="verify-title"
          >
            {purpose === 'register' ? 'Registrierung bestätigen' : 'Anmeldung bestätigen'}
          </h1>
          <p className="text-zinc-400 text-sm leading-relaxed">
            Wir haben einen 6-stelligen Code an{' '}
            <span className="text-zinc-200 font-medium">{email}</span> geschickt.
            Bitte unten eingeben — der Code ist 10 Minuten gültig.
          </p>
        </div>

        {error && (
          <div className="mb-5 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm flex items-center gap-2" data-testid="verify-error">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}
        {resendStatus && !error && (
          <div className="mb-5 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg text-emerald-300 text-sm">
            {resendStatus}
          </div>
        )}

        {/* Code-Inputs */}
        <div className="flex justify-center gap-2 sm:gap-3 mb-6" data-testid="verify-code-inputs">
          {digits.map((d, i) => (
            <input
              key={i}
              ref={refs[i]}
              type="text"
              inputMode="numeric"
              maxLength={1}
              value={d}
              onChange={(e) => updateDigit(i, e.target.value)}
              onKeyDown={(e) => onKeyDown(i, e)}
              onPaste={i === 0 ? onPaste : undefined}
              data-testid={`verify-digit-${i}`}
              className="w-11 h-14 sm:w-12 sm:h-16 text-center text-2xl sm:text-3xl font-mono font-bold bg-zinc-900 border border-zinc-800 rounded-lg text-zinc-100 focus:border-blue-500 focus:outline-none transition-colors"
              disabled={loading}
            />
          ))}
        </div>

        {loading && (
          <div className="flex items-center justify-center text-zinc-400 text-sm gap-2 mb-4">
            <Loader2 className="w-4 h-4 animate-spin" /> Code wird geprüft...
          </div>
        )}

        <div className="text-center text-sm">
          <span className="text-zinc-500">Keinen Code erhalten? </span>
          <button
            onClick={onResend}
            disabled={cooldown > 0 || loading}
            data-testid="verify-resend-btn"
            className="text-blue-400 hover:text-blue-300 disabled:text-zinc-600 disabled:cursor-not-allowed underline underline-offset-2 transition-colors"
          >
            {cooldown > 0 ? `Erneut senden (${cooldown}s)` : 'Erneut senden'}
          </button>
        </div>
      </div>
    </div>
  );
}
